import {
  type Ga4CartEvent,
  buildGa4CartEvent,
} from "~/hec/analytics/event-layer/gtm-cart-events";
import type { SelectItemEvent } from "~/hec/analytics/event-layer/gtm-select-item-event";

import type { CartUpdateEventData } from "./cart-update";

/** The `/resources/basket` response slice the Cart Update tracker reads. */
export type CartUpdateResponseData = {
  segmentCartUpdate?: CartUpdateEventData[];
  gtmAddContext?: { clickSource?: string; quickView?: boolean };
  gtmSelectItem?: SelectItemEvent;
};

export type CartUpdateDispatchPlan = {
  /** ONE GA4 cart event per basket action, every line an item (SFRA
   *  Cart.js:344-355); null when the events build no GA4 event. */
  ga4: Ga4CartEvent | null;
  /** AB Tasty Universal Collect EVENT hit (SFCC-12079). Scoped to add/remove
   *  deliberately: SFCC-12175 W5 made `modify` produce a GA4 event, and a hit
   *  riding on `ga4` would have silently started counting quantity edits in
   *  Flagship KPIs — a martech decision, not ours. Null for `modify` or no GA4. */
  abTasty: {
    ec: "Action Tracking";
    ea: "add_to_cart" | "remove_from_cart";
  } | null;
  /** GA4 `select_item` (CYP cross-sell add), one per addMany response. */
  selectItem: SelectItemEvent | null;
  /** Segment "Cart Update" stays intentionally per-line. */
  segmentEvents: CartUpdateEventData[];
};

/**
 * What the Cart Update tracker should emit for one settled basket submission.
 * Pure: the tracker only performs the side effects. Returns null when the
 * payload carries no Cart Update events (nothing to track).
 */
export function planCartUpdateDispatch(
  data: unknown,
): CartUpdateDispatchPlan | null {
  if (!data || typeof data !== "object") return null;
  const response = data as CartUpdateResponseData;
  const events = response.segmentCartUpdate;
  if (!events || events.length === 0) return null;

  const ga4 = buildGa4CartEvent(events, {
    source: response.gtmAddContext?.clickSource,
    quickView: response.gtmAddContext?.quickView,
  });
  const abTasty =
    ga4 && events[0].action !== "modify"
      ? ({
          ec: "Action Tracking",
          ea:
            ga4.event === "GA4_removeFromCart"
              ? "remove_from_cart"
              : "add_to_cart",
        } as const)
      : null;

  return {
    ga4: ga4 ?? null,
    abTasty,
    selectItem: response.gtmSelectItem ?? null,
    segmentEvents: events,
  };
}
